import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Home, Users, Inbox, LogOut, Menu, X, ExternalLink, BarChart3,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/job_guest-retreat-finder/artifacts/ymwgc3ta_Logo%20Quattro%20Accommodations.png";

const NAV = [
  { label: "Dashboard", path: "/admin", icon: LayoutDashboard, testId: "admin-nav-dashboard" },
  { label: "Strutture", path: "/admin/properties", icon: Home, testId: "admin-nav-properties" },
  { label: "Proprietari", path: "/admin/owners", icon: Users, testId: "admin-nav-owners" },
  { label: "Richieste", path: "/admin/contacts", icon: Inbox, testId: "admin-nav-contacts" },
  { label: "Report", path: "/admin/report", icon: BarChart3, testId: "admin-nav-report" },
];

export default function AdminLayout({ children, title }) {
  const { pathname } = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const isActive = (path) => (path === "/admin" ? pathname === "/admin" : pathname.startsWith(path));

  const Sidebar = (
    <div className="flex flex-col h-full bg-brand-violet text-white">
      <div className="p-6 border-b border-white/10">
        <img src={LOGO} alt="Quattro" className="h-9 w-auto object-contain bg-white/95 rounded-lg p-1" />
        <p className="text-xs text-white/40 mt-2 font-mono">Admin Panel</p>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {NAV.map((n) => (
          <Link
            key={n.path}
            to={n.path}
            onClick={() => setOpen(false)}
            data-testid={n.testId}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
              isActive(n.path) ? "bg-brand-teal text-brand-violet" : "text-white/70 hover:bg-white/10"
            }`}
          >
            <n.icon size={18} /> {n.label}
          </Link>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10 space-y-2">
        <Link to="/" target="_blank" className="flex items-center gap-2 px-4 py-2 text-sm text-white/60 hover:text-brand-teal">
          <ExternalLink size={16} /> Vedi il portale
        </Link>
        <div className="px-4 text-xs text-white/40 truncate">{user?.email}</div>
        <Button onClick={doLogout} data-testid="admin-logout-btn" variant="ghost" className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10">
          <LogOut size={16} className="mr-2" /> Esci
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-canvas flex">
      <aside className="hidden lg:block w-64 shrink-0 fixed inset-y-0">{Sidebar}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64">{Sidebar}</div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex-1 lg:ml-64">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-brand-plum/10 h-16 flex items-center px-4 sm:px-6 gap-3">
          <button className="lg:hidden text-brand-plum" onClick={() => setOpen(true)}><Menu size={24} /></button>
          <h1 className="font-serif text-xl md:text-2xl text-brand-plum">{title}</h1>
        </header>
        <div className="p-4 sm:p-6 lg:p-8">{children}</div>
      </div>
    </div>
  );
}
